import React, { useState } from 'react';

const COLORS = {
  bg: '#0d1117',
  card: '#161b22',
  border: '#30363d',
  text: '#c9d1d9',
  textMuted: '#8b949e',
  green: '#3fb950',
  amber: '#d29922',
  red: '#f85149',
};

export default function SignalGauge({ composite_score, article_count, signal }) {
  const [showInfo, setShowInfo] = useState(false);
  const score = composite_score ?? 0;
  const sellThresh = -0.25;
  const buyThresh = 0.60;
  const distToSell = Math.abs(score - sellThresh);
  const distToBuy = Math.abs(score - buyThresh);

  // Map score from [-1, 1] to [0, 400] for SVG
  const barW = 400;
  const barH = 28;
  const needleX = ((score + 1) / 2) * barW;
  const sellX = ((sellThresh + 1) / 2) * barW;
  const buyX = ((buyThresh + 1) / 2) * barW;

  // Confidence
  const count = article_count ?? 0;
  let confLevel, confColor, confEmoji;
  if (count < 15) { confLevel = 'LOW'; confColor = COLORS.red; confEmoji = '🔴'; }
  else if (count <= 35) { confLevel = 'MEDIUM'; confColor = COLORS.amber; confEmoji = '🟡'; }
  else { confLevel = 'HIGH'; confColor = COLORS.green; confEmoji = '🟢'; }

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 24, marginBottom: 24 }}>
      <div style={{ color: COLORS.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
        Signal Proximity Gauge
      </div>

      <svg width="100%" viewBox={`0 0 ${barW + 40} 80`} style={{ display: 'block', maxWidth: 600, margin: '0 auto' }}>
        {/* SELL zone */}
        <rect x="20" y="20" width={sellX} height={barH} rx="4" fill={COLORS.red} opacity="0.7" />
        {/* HOLD zone */}
        <rect x={20 + sellX} y="20" width={buyX - sellX} height={barH} fill={COLORS.amber} opacity="0.7" />
        {/* BUY zone */}
        <rect x={20 + buyX} y="20" width={barW - buyX} height={barH} rx="4" fill={COLORS.green} opacity="0.7" />

        {/* Zone labels */}
        <text x={20 + sellX / 2} y="38" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">SELL</text>
        <text x={20 + (sellX + buyX) / 2} y="38" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">HOLD</text>
        <text x={20 + (buyX + barW) / 2} y="38" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">BUY</text>

        {/* Needle */}
        <polygon
          points={`${20 + needleX - 6},18 ${20 + needleX + 6},18 ${20 + needleX},26`}
          fill="#fff"
        />
        <line x1={20 + needleX} y1="18" x2={20 + needleX} y2={20 + barH} stroke="#fff" strokeWidth="2" />

        {/* Score label */}
        <text x={20 + needleX} y="12" textAnchor="middle" fill={COLORS.text} fontSize="11" fontWeight="700" fontFamily="monospace">
          {score.toFixed(2)}
        </text>

        {/* Distance labels */}
        <text x="20" y="65" fill={COLORS.red} fontSize="10" fontFamily="monospace">
          {distToSell.toFixed(2)} from SELL
        </text>
        <text x={barW + 20} y="65" textAnchor="end" fill={COLORS.green} fontSize="10" fontFamily="monospace">
          {distToBuy.toFixed(2)} from BUY
        </text>
      </svg>

      {/* Confidence badge */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: `${confColor}22`,
          border: `1px solid ${confColor}`,
          borderRadius: 20,
          padding: '6px 18px',
          fontSize: 13,
          fontWeight: 600,
          color: confColor,
        }}>
          {confEmoji} {confLevel} CONFIDENCE · {count} articles
        </span>
      </div>

      {/* Threshold explanation */}
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <button
          onClick={() => setShowInfo(!showInfo)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: COLORS.textMuted, fontSize: 11, padding: '2px 0',
          }}
        >
          {showInfo ? '▲ Hide' : 'ℹ️ Why are the thresholds asymmetric?'}
        </button>
        {showInfo && (
          <div style={{
            marginTop: 10, padding: '12px 16px',
            background: '#0d1117', borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            fontSize: 12, color: COLORS.textMuted,
            textAlign: 'left', lineHeight: 1.6,
          }}>
            <p style={{ margin: '0 0 8px' }}>
              <strong style={{ color: COLORS.text }}>SELL triggers at −0.25, BUY at +0.60</strong> — the thresholds are intentionally asymmetric for three reasons:
            </p>
            <p style={{ margin: '0 0 8px' }}>
              <strong style={{ color: COLORS.text }}>News skews negative.</strong> Financial articles are written more alarmingly in bad times than optimistically in good ones. Requiring +0.60 for BUY filters out "slightly less bad" noise and demands genuine broad optimism.
            </p>
            <p style={{ margin: '0 0 8px' }}>
              <strong style={{ color: COLORS.text }}>Downside moves are faster.</strong> Markets crash in days and recover over months. A SELL threshold at −0.25 catches deterioration early. Waiting for −0.60 would miss most of the move.
            </p>
            <p style={{ margin: 0 }}>
              <strong style={{ color: COLORS.text }}>Loss asymmetry.</strong> Missing a rally (false SELL) costs less than buying into a crash (false BUY). The thresholds are conservative in the bearish direction and demanding in the bullish direction.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
